import { env } from "@/lib/env";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type ApiUser = {
  id: string;
  email: string;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  role: "ADMIN" | "CLIENT";
  isActive: boolean;
};

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(data.message)) {
      return data.message.join(", ");
    }
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
  } catch {
    // ignore JSON parse errors
  }
  return response.statusText || "Request failed";
}

const DEFAULT_TIMEOUT_MS = 20_000;

export async function apiRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const baseUrl = env.apiBaseUrl.replace(/\/$/, "");
  if (!baseUrl) {
    throw new ApiError(500, "VITE_API_BASE_URL is not configured");
  }

  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(
    () => timeoutController.abort(),
    DEFAULT_TIMEOUT_MS,
  );

  const upstreamSignal = init?.signal;
  const onUpstreamAbort = () => timeoutController.abort();
  upstreamSignal?.addEventListener("abort", onUpstreamAbort);

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      signal: timeoutController.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw new ApiError(response.status, await parseErrorMessage(response));
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(
        408,
        "Sign-in timed out. Check that the API can reach the database, then try again.",
      );
    }
    throw new ApiError(
      0,
      error instanceof Error
        ? error.message
        : "Unable to reach the API. Is the backend running?",
    );
  } finally {
    window.clearTimeout(timeoutId);
    upstreamSignal?.removeEventListener("abort", onUpstreamAbort);
  }
}

export const api = {
  getMe: () => apiRequest<ApiUser>("/users/me"),
  refreshSession: () =>
    apiRequest<{ user: ApiUser }>("/auth/refresh", { method: "POST" }),
  loginWithGoogle: (idToken: string) =>
    apiRequest<{ user: ApiUser }>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ idToken }),
    }),
  logout: () =>
    apiRequest<{ message: string }>("/auth/logout", {
      method: "POST",
    }),
};

export type { ApiUser };
